import { IDropdownOption } from '@fluentui/react';
import { DateInput } from 'components/controls/DateInput';
import { cloneDeep } from 'lodash';
import Select from 'components/controls/Select';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import FilterBar from 'components/layout/FilterBar';
import { ReadOn, usePermissions, UserPermission } from 'modules/permissions';
import { useCallback, useEffect, useState } from 'react';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { Opportunity, OpportunityListQuery } from '../../../queries/opportunity-list.queries.types';
import {
   OpportunityDepotLookup,
   OpportunityDivisionLookup,
   OpportunityRegionLookup,
   SHEQManagerLookup,
   OpportunityEnumLookup,
   OpportunityFilterLookup
} from '../types/opportunity-filter.types';
import { isEmpty, isNullOrEmpty, isNullOrUndefined } from 'shared/utils/null.utils';
import { FilterSelectV2 } from 'components/controls/Select';
import { getCookie, setCookie, setDateFromCookie } from 'shared/utils/cookies.utils';
import { ObservationListFilterEnum } from 'shared/enums/ObservationListFilter.enum';

interface OpportunityFilterBarProps {
   filterDataLoaded: boolean;
   showFilterBar: boolean;
   toggleFilterBar: () => void;
   filterRequest: OpportunityListQuery;
   data: Opportunity[] | undefined;
   setFilterRequest: (newFilter: OpportunityListQuery) => void;
   resetFilter: () => void;
}

const defaultLookupValues: OpportunityFilterLookup = {
   statuses: [],
   depots: [],
   divisions: [],
   regions: [],
   sHEQManagers: []
};

export const OpportunitiesFilterBar = ({
   filterDataLoaded,
   showFilterBar,
   toggleFilterBar,
   filterRequest,
   data: data,
   setFilterRequest,
   resetFilter
}: OpportunityFilterBarProps) => {
   const [lookupValues, setLookUpValues] = useState<OpportunityFilterLookup | undefined>();
   const [originalLookupValues, setOriginalLookUpValues] = useState<OpportunityFilterLookup | undefined>();

   const { hasPermission } = usePermissions();

   useEffect(() => {
      if (!data) {
         return;
      }

      let dateFrom = setDateFromCookie(getCookie(ObservationListFilterEnum.OpportunityDateFrom));
      let dateTo = setDateFromCookie(getCookie(ObservationListFilterEnum.OpportunityDateTo));
      let observationStatus = (getCookie(ObservationListFilterEnum.OpportunityStatus)?.split(
         ','
      ) as ObservationStatusEnum[]) ?? [ObservationStatusEnum.Open, ObservationStatusEnum.Closed];
      let regionId = getCookie(ObservationListFilterEnum.OpportunityRegionIds)?.split(',').map(Number) ?? [];
      let divisionId = getCookie(ObservationListFilterEnum.OpportunityDivisionIds)?.split(',').map(Number) ?? [];
      let depotPcId = getCookie(ObservationListFilterEnum.OpportunityDepotPcId)?.split(',').map(Number) ?? [];
      let sHEQManagerId = getCookie(ObservationListFilterEnum.OpportunityManagerIds)?.split(',').map(Number) ?? [];

      setFilterRequest({
         ...filterRequest,
         dateFrom: dateFrom,
         dateTo: dateTo,
         observationStatus: observationStatus,
         regionId: regionId,
         divisionId: divisionId,
         depotPcId: depotPcId,
         sHEQManagerId: sHEQManagerId
      });

      if (
         !isNullOrEmpty(dateFrom) ||
         !isNullOrEmpty(dateTo) ||
         (observationStatus.toString() != 'OPEN,CLOSED' && observationStatus.toString() != 'CLOSED,OPEN') ||
         regionId.length > 0 ||
         divisionId.length > 0 ||
         depotPcId.length > 0 ||
         sHEQManagerId.length > 0
      ) {
         toggleFilterBar();
      }
      const newLookupValues = cloneDeep<OpportunityFilterLookup>(lookupValues ?? defaultLookupValues);

      newLookupValues.statuses = data
         .map(x => {
            return {
               id: x.observationStatus as ObservationStatusEnum,
               name: x.observationStatus.toProperCase()
            };
         })
         .distinct(x => x.id);

      if (
         !newLookupValues?.statuses.any(s => s.id === ObservationStatusEnum.Archived) &&
         hasPermission(ReadOn(UserPermission.Archive))
      ) {
         newLookupValues.statuses.push({
            id: ObservationStatusEnum.Archived,
            name: ObservationStatusEnum.Archived.toProperCase()
         });
      }

      newLookupValues.depots = data
         .filter(x => x.depotPcName != null)
         .map(x => {
            return {
               regionId: x.regionId,
               divisionId: x.divisionId,
               pcId: x.depotPcId,
               pc: x.depotPC,
               pcName: x.depotPcName
            };
         })
         .distinct(x => x.pcName)
         .sort((a: OpportunityDepotLookup, b: OpportunityDepotLookup) => {
            const sortValueA = a.pc;
            const sortValueB = b.pc;

            return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
         });

      newLookupValues.divisions = data
         .filter(x => x.divisionName != null)
         .map(x => {
            return {
               regionId: x.regionId,
               divisionId: x.divisionId,
               divisionName: x.divisionName
            };
         })
         .distinct(x => x.divisionId)
         .sort((a: OpportunityDivisionLookup, b: OpportunityDivisionLookup) => {
            const sortValueA = a.divisionName;
            const sortValueB = b.divisionName;

            return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
         });

      newLookupValues.regions = data
         .filter(x => x.regionName != null)
         .map(x => {
            return {
               regionId: x.regionId,
               regionName: x.regionName
            };
         })
         .distinct(x => x.regionId)
         .sort((a: OpportunityRegionLookup, b: OpportunityRegionLookup) => {
            const sortValueA = a.regionName;
            const sortValueB = b.regionName;

            return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
         });

      newLookupValues.sHEQManagers = data
         .filter(x => x.sHEQManager != null)
         .map(x => {
            return {
               sHEQManagerId: x.sHEQManagerId,
               sHEQManager: x.sHEQManager
            };
         })
         .distinct(x => x.sHEQManagerId)
         .sort((a: SHEQManagerLookup, b: SHEQManagerLookup) => {
            const sortValueA = a.sHEQManager;
            const sortValueB = b.sHEQManager;

            return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
         });

      setLookUpValues(newLookupValues);
      setOriginalLookUpValues(newLookupValues);
   }, [filterDataLoaded]);

   const onResetFilter = () => {
      const newLookupValues = cloneDeep<OpportunityFilterLookup>(lookupValues ?? defaultLookupValues);
      newLookupValues.divisions = originalLookupValues?.divisions;
      newLookupValues.depots = originalLookupValues?.depots;
      setLookUpValues(newLookupValues);

      resetCookies();
      resetFilter();
   };

   const onDateFromSelect = (date: Date | null | undefined) => {
      setCookie(ObservationListFilterEnum.OpportunityDateFrom, date);

      setFilterRequest({
         ...filterRequest,
         dateFrom: date ?? undefined
      });
   };

   const onDateToSelect = (date: Date | null | undefined) => {
      if (!isNullOrUndefined(date)) {
         let toDate = new Date(date.setHours(23, 59, 59, 999));

         setCookie(ObservationListFilterEnum.OpportunityDateTo, toDate);

         setFilterRequest({
            ...filterRequest,
            dateTo: toDate ?? undefined
         });
      }
   };

   const onDepotSelect = (options?: IDropdownOption[]) => {
      let depotPcId = options?.map(x => Number(x.key));

      setCookie(ObservationListFilterEnum.OpportunityDepotPcId, depotPcId);

      setFilterRequest({
         ...filterRequest,
         depotPcId: depotPcId
      });
   };

   const onDivisionSelect = (options?: IDropdownOption[]) => {
      let divisionIds = options?.map(x => Number(x.key));

      setCookie(ObservationListFilterEnum.OpportunityDivisionIds, divisionIds);
      setCookie(ObservationListFilterEnum.OpportunityDepotPcId, '');

      setFilterRequest({
         ...filterRequest,
         divisionId: divisionIds,
         depotPcId: []
      });

      let depots =
         originalLookupValues?.depots?.filter(x => divisionIds?.any(r => r === x.divisionId))?.distinct(x => x.pcId) ??
         [];

      const newLookupValues = cloneDeep<OpportunityFilterLookup>(lookupValues ?? defaultLookupValues);
      newLookupValues.depots = depots;
      setLookUpValues(newLookupValues);
   };

   const onRegionSelect = (options?: IDropdownOption[]) => {
      let regionIds = options?.map(x => Number(x.key)) ?? [];

      setCookie(ObservationListFilterEnum.OpportunityRegionIds, regionIds);
      setCookie(ObservationListFilterEnum.OpportunityDivisionIds, '');
      setCookie(ObservationListFilterEnum.OpportunityDepotPcId, '');

      setFilterRequest({
         ...filterRequest,
         regionId: regionIds,
         divisionId: [],
         depotPcId: []
      });

      let divisions: OpportunityDivisionLookup[] | undefined = [];
      let depots: OpportunityDepotLookup[] | undefined = [];
      if (isEmpty(regionIds)) {
         divisions = originalLookupValues?.divisions ?? [];
         depots = originalLookupValues?.depots ?? [];
      } else {
         divisions =
            originalLookupValues?.divisions
               ?.filter(x => regionIds?.any(r => r === x.regionId))
               ?.distinct(x => x.divisionId) ?? [];

         depots =
            originalLookupValues?.depots?.filter(x => regionIds?.any(r => r === x.regionId))?.distinct(x => x.pcId) ??
            [];
      }

      const newLookupValues = cloneDeep<OpportunityFilterLookup>(lookupValues ?? defaultLookupValues);
      newLookupValues.divisions = divisions;
      newLookupValues.depots = depots;
      setLookUpValues(newLookupValues);
   };

   const onSHEQManagerSelect = (options?: IDropdownOption[]) => {
      let managerIds = options?.map(x => Number(x.key));

      setCookie(ObservationListFilterEnum.OpportunityManagerIds, managerIds);

      setFilterRequest({
         ...filterRequest,
         sHEQManagerId: managerIds
      });
   };

   const onStatusSelect = (event: React.FormEvent<HTMLDivElement> | undefined, option?: IDropdownOption) => {
      if (option) {
         let selectedStatuses = filterRequest.observationStatus;

         const alreadySelected = selectedStatuses?.any(x => x === (option!.key as ObservationStatusEnum));

         if (option.selected && !alreadySelected) {
            selectedStatuses!.push(option!.key as ObservationStatusEnum);
         }

         if (!option.selected && alreadySelected) {
            selectedStatuses = selectedStatuses?.filter(x => x !== (option!.key as ObservationStatusEnum));
         }

         setCookie(ObservationListFilterEnum.OpportunityStatus, selectedStatuses);

         setFilterRequest({
            ...filterRequest,
            observationStatus: selectedStatuses
         });
      }
   };

   const resetCookies = () => {
      setCookie(ObservationListFilterEnum.OpportunityDateFrom, '');
      setCookie(ObservationListFilterEnum.OpportunityDateTo, '');
      setCookie(ObservationListFilterEnum.OpportunityCategoryId, '');
      setCookie(ObservationListFilterEnum.OpportunityStatus, 'OPEN,CLOSED');
      setCookie(ObservationListFilterEnum.OpportunityRegionIds, '');
      setCookie(ObservationListFilterEnum.OpportunityDivisionIds, '');
      setCookie(ObservationListFilterEnum.OpportunityDepotPcId, '');
      setCookie(ObservationListFilterEnum.OpportunityManagerIds, '');
   };

   const showSHEQTeamFilter = useCallback(() => {
      return hasPermission(ReadOn(UserPermission.DepotLinks));
   }, [hasPermission]);

   return (
      <ConditionalRender condition={showFilterBar}>
         <FilterBar showReset onReset={onResetFilter}>
            <DateInput
               placeholder={'Date From'}
               borderless
               restrictWidth
               onDateSelect={onDateFromSelect}
               value={filterRequest.dateFrom?.toDateString()}
               transparent
               useEnglish
            />
            <DateInput
               placeholder={'Date To'}
               borderless
               restrictWidth
               onDateSelect={onDateToSelect}
               value={filterRequest.dateTo?.toDateString()}
               transparent
               useEnglish
            />

            <Select
               style={{ minWidth: '140px', marginTop: '-8px', marginLeft: '0px' }}
               options={mapToDropdownOptions<OpportunityEnumLookup<ObservationStatusEnum>, IDropdownOption>(
                  lookupValues?.statuses,
                  status => status.id.toUpperCase(),
                  status => `${status.name.toProperCase()}`)
               }
               transparent
               multiSelect
               placeholder={'Status'}
               selectedKeys={filterRequest.observationStatus ?? []}
               onChange={onStatusSelect}
               calloutWidth='auto'
            />
         </FilterBar>
           {
               <ConditionalRender condition={showSHEQTeamFilter}>
                   <FilterBar>
                       <FilterSelectV2
                           style={{ minWidth: '120px', marginTop: '-2px', marginLeft: '10px' }}
                           options={mapToDropdownOptions<OpportunityRegionLookup, IDropdownOption>(
                               lookupValues?.regions,
                               region => region.regionId,
                               region => `${region.regionName}`
                           )}
                           transparent
                           multiSelect
                           placeholder={'Region'}
                           selectedKeys={filterRequest.regionId}
                           onChangeMulti={onRegionSelect}
                       />

                       <FilterSelectV2
                           style={{ minWidth: '160px', marginTop: '-2px' }}
                           options={mapToDropdownOptions<OpportunityDivisionLookup, IDropdownOption>(
                               lookupValues?.divisions,
                               division => division.divisionId,
                               division => `${division.divisionName}`
                           )}
                           transparent
                           multiSelect
                           placeholder={'Division'}
                           selectedKeys={filterRequest.divisionId}
                           onChangeMulti={onDivisionSelect}
                       />

                       <FilterSelectV2
                           style={{ minWidth: '300px', marginTop: '-2px' }}
                           options={mapToDropdownOptions<OpportunityDepotLookup, IDropdownOption>(
                               lookupValues?.depots,
                               depot => depot.pcId,
                               depot => `${depot.pcName}`
                           )}
                           transparent
                           multiSelect
                           placeholder={'Depot'}
                           selectedKeys={filterRequest.depotPcId}
                           onChangeMulti={onDepotSelect}
                       />

                       <FilterSelectV2
                           style={{ minWidth: '100px', marginTop: '-2px' }}
                           options={mapToDropdownOptions<SHEQManagerLookup, IDropdownOption>(
                               lookupValues?.sHEQManagers,
                               sheqManager => sheqManager.sHEQManagerId,
                               sheqManager => `${sheqManager.sHEQManager}`
                           )}
                           transparent
                           multiSelect
                           placeholder={'SHEQ Manager'}
                           selectedKeys={filterRequest.sHEQManagerId}
                           onChangeMulti={onSHEQManagerSelect}
                       />
                   </FilterBar>
               </ConditionalRender>
           }
      </ConditionalRender>
   );
};
